/**
 * Bluff Ratio quiz — the bettor's-side complement of pot odds.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { Stat } from "@/features/quiz/components/Stat";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  type BluffRatioQuestion,
  describeFraction,
  generateQuestion,
} from "@/features/quiz/lib/bluffRatioQuiz";

export function QuizBluffRatio() {
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } =
    useQuizSession<BluffRatioQuestion, number>({
      quizType: "bluff_ratio",
      generate: generateQuestion,
      getCorrectAnswer: (q) => q.options[q.correctIndex]!,
      getQuestionPayload: (q) => ({
        pot: q.pot,
        bet: q.bet,
        bet_fraction: q.betFraction,
      }),
    });

  return (
    <QuizLayout
      Icon={Brain}
      title="Bluff Ratio"
      subtitle="Bet sizing → % bluffs for balance"
      description="Tests how often villain's range should be bluffs at each bet size for their range to be balanced. Same formula as pot odds, opposite seat: once you have these numbers cached you can read whether villain is over- or under-bluffing and adjust your defending range accordingly."
      stats={stats}
    >
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="flex items-baseline gap-8">
          <Stat label="POT" value={`${question.pot}bb`} />
          <Stat label="VILLAIN BETS" value={`${question.bet}bb`} accent />
        </div>
      </div>

      <p className="text-center text-text-muted text-sm">
        For a balanced range, what % of villain's bets should be bluffs?
      </p>

      <div className="grid grid-cols-2 gap-3">
        {question.options.map((pct) => (
          <QuizOption
            key={pct}
            value={pct}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="text-lg">{pct}%</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{correctAnswer}% bluffs</strong>
            </>
          }
          rationale={
            <>
              A {describeFraction(question.betFraction)} bet ({question.bet}bb into {question.pot}bb) needs ~{correctAnswer}% bluffs for balance. Same math as pot odds: bet / (pot + 2×bet) = bluff frequency = hero's required calling equity.
            </>
          }
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}

